import type { DraftAction } from '../../models/aiAnalysis/workflow'

interface DatadogRumAgent {
  onReady: (callback: () => void) => void
  addAction?: (name: string, context?: object) => void
}

declare global {
  interface Window {
    DD_RUM?: DatadogRumAgent
  }
}

export const AI_SECTION_EVENTS = {
  CARD_SHOWN: 'ai_section.card_shown',
  TAKE: 'ai_section.take',
  RELEASE: 'ai_section.release',
  DECIDE: 'ai_section.decide'
} as const

export type AiSectionEvent = typeof AI_SECTION_EVENTS[keyof typeof AI_SECTION_EVENTS]

// Context attributes are searchable facets; draft text never belongs in one.
export type SectionEventContext = Record<string, string | number | null>

// onReady queues the callback behind the async loader stub; the agent runs it later on its own stack.
export const trackSectionEvent = (name: AiSectionEvent, context: SectionEventContext): void => {
  try {
    const agent = window.DD_RUM
    if (typeof agent?.onReady !== 'function') return

    agent.onReady(() => {
      try {
        window.DD_RUM?.addAction?.(name, context)
      } catch {
        // Telemetry never changes what the scheduler sees.
      }
    })
  } catch {
    // Telemetry never changes what the scheduler sees.
  }
}

// One card_shown per draft per page load, however often draft events remount the section.
const shownCardIds = new Set<string>()

export const markCardShown = (actionId: string): boolean => {
  if (shownCardIds.has(actionId)) return false

  shownCardIds.add(actionId)
  return true
}

export const _resetFunnelMemory = (): void => shownCardIds.clear()

export const draftAgeSeconds = (action: Pick<DraftAction, 'created_at'>, now: number = Date.now()): number | null => {
  const createdAt = Date.parse(action.created_at)
  if (Number.isNaN(createdAt)) return null

  return Math.floor((now - createdAt) / 1000)
}
